import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CreditCard, LogOut, User, ChevronDown, Coins, Gift, Receipt, Loader2, Shield } from 'lucide-react';
import { UserProfile } from '../types';
import { ProductSwitcher, MobileAppSwitcher } from './AppSwitcher';
import { openBillingPortal } from '../services/supabase';
import { formatCreditAmount, HDR_PRICING_COPY } from '@rehub/pricing';
import HdrUsageCard from './HdrUsageCard';
import type { HdrEntitlement } from '../services/hdr';

interface HeaderProps {
  user: UserProfile | null;
  onSignOut: () => void;
  onRefreshCredits: () => void;
  onOpenBuyCredits?: () => void;
  isAdmin?: boolean;
  hdrEntitlement?: HdrEntitlement | null;
}

function formatCredits(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  // Show fractional balances correctly (e.g. 12.5), integers stay clean.
  return formatCreditAmount(n);
}

const Header: React.FC<HeaderProps> = ({ user, onSignOut, onRefreshCredits, onOpenBuyCredits, isAdmin, hdrEntitlement }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingNotice, setBillingNotice] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleBillingPortal = async () => {
    if (billingLoading) return;
    setBillingNotice(null);
    setBillingLoading(true);
    try {
      const result = await openBillingPortal();
      if ('noCustomer' in result) {
        setBillingNotice("No invoices yet — buy credits or a plan and they'll appear here.");
      } else {
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('[Billing] portal failed:', err);
      setBillingNotice("Couldn't open billing right now. Please try again.");
    } finally {
      setBillingLoading(false);
    }
  };

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <header className="sticky top-0 left-0 right-0 z-40 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/80">
      <div className="flex items-center gap-3 h-16 px-4 sm:px-6">
        {/* Left: cross-app switcher ties HDR Photo into the wider ReHub suite */}
        <div className="flex items-center min-w-0">
          <ProductSwitcher currentApp="hdrphoto" />
        </div>

        {/* Right: HDR Credits + ReHub Credits + account */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0 ml-auto">
          <HdrUsageCard entitlement={hdrEntitlement ?? null} compact />

          {/* ReHub Credits pill — other ReHub apps, not HDR processing */}
          <button
            onClick={onOpenBuyCredits ?? onRefreshCredits}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 border border-slate-800 hover:border-brand-500/50 hover:bg-slate-800/80 transition-all text-sm"
            title={HDR_PRICING_COPY.aiCreditsLabel}
          >
            <Coins size={15} className="text-amber-400" />
            <span className="font-semibold text-slate-100">{formatCredits(user?.credits || 0)}</span>
            <span className="text-slate-500 hidden sm:inline">{HDR_PRICING_COPY.aiCreditsLabel}</span>
          </button>

          {/* Give 50, Get 50 */}
          <Link
            to="/referrals"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-500/15 border border-purple-500/30 text-purple-300 hover:bg-purple-500/25 hover:text-purple-200 transition-all text-sm font-medium"
          >
            <Gift size={14} />
            <span>Refer &amp; Earn</span>
          </Link>

          {/* Notifications */}
          <button className="relative p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors">
            <Bell size={18} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-brand-500 rounded-full ring-2 ring-slate-950" />
          </button>

          {/* User dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown((o) => !o)}
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-slate-800 transition-colors"
              aria-haspopup="true"
              aria-expanded={showDropdown}
            >
              <div className="w-8 h-8 bg-gradient-to-br from-brand-400 to-brand-600 rounded-full flex items-center justify-center ring-1 ring-white/10">
                <User size={16} className="text-white" />
              </div>
              <span className="hidden lg:block text-sm text-slate-300 max-w-[150px] truncate">
                {user?.email || 'Guest'}
              </span>
              <ChevronDown size={16} className={`text-slate-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showDropdown && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 py-2 animate-fade-in">
                <div className="px-4 py-2 border-b border-slate-800">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.email}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {hdrEntitlement?.plan_name || 'Free HDR plan'}
                    {user?.subscription_plan ? ` · ${user.subscription_plan}` : ''}
                  </p>
                </div>

                {isAdmin && (
                  <Link
                    to="/admin/processing"
                    onClick={() => setShowDropdown(false)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-brand-300 hover:bg-slate-800 transition-colors"
                  >
                    <Shield size={16} />
                    Admin
                  </Link>
                )}

                <Link
                  to="/pricing"
                  onClick={() => setShowDropdown(false)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 transition-colors"
                >
                  <CreditCard size={16} />
                  HDR plans
                </Link>

                <button
                  onClick={() => {
                    setShowDropdown(false);
                    onOpenBuyCredits?.();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 transition-colors"
                >
                  <CreditCard size={16} />
                  Buy ReHub Credits
                </button>

                <Link
                  to="/referrals"
                  onClick={() => setShowDropdown(false)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-purple-300 hover:bg-slate-800 transition-colors"
                >
                  <Gift size={16} />
                  Refer &amp; Earn
                </Link>

                <button
                  onClick={handleBillingPortal}
                  disabled={billingLoading}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 transition-colors disabled:opacity-60"
                >
                  {billingLoading ? <Loader2 size={16} className="animate-spin" /> : <Receipt size={16} />}
                  Billing &amp; invoices
                </button>
                {billingNotice && (
                  <div className="px-4 py-2 text-xs text-amber-300/90">{billingNotice}</div>
                )}

                <button
                  onClick={() => {
                    setShowDropdown(false);
                    onSignOut();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-slate-800 transition-colors"
                >
                  <LogOut size={16} />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile: cross-app switcher row (sidebar handles in-app nav) */}
      <div className="md:hidden border-t border-slate-800/80 py-2 flex items-center justify-center gap-3 overflow-x-auto">
        <MobileAppSwitcher currentApp="hdrphoto" />
      </div>
    </header>
  );
};

export default Header;
